/**
 * Constantes del feature "inventarios"
 *
 * Nombres de dominio en español (INVENTARIO_ESTADO, ETIQUETAS_ESTADO) y
 * modificadores técnicos en inglés, según la sección "Idioma de los Nombres"
 * de CLAUDE.md.
 */

import { BADGE_COLORS } from "../../components/ui/badgeColors";

/**
 * Estados de una lista de inventario, en el orden en que avanza su máquina
 * de estados: PENDIENTE -> EN_CURSO -> COMPLETADO -> REVISADO.
 *
 * Mismo patrón que TRASPASO_ESTADO en src/config/constants.js: cada valor es
 * igual a su propia clave, en mayúsculas, para que el literal que viaja al
 * backend sea el mismo que el nombre que se lee en el código.
 *
 * La transición a EN_CURSO la hace el backend en el primer conteo registrado;
 * el paso a REVISADO es una acción explícita del
 * admin sobre un inventario COMPLETADO (ver tasks/milestones.md, decisión 8).
 */
export const INVENTARIO_ESTADO = {
    PENDIENTE: "PENDIENTE",
    EN_CURSO: "EN_CURSO",
    COMPLETADO: "COMPLETADO",
    REVISADO: "REVISADO",
};

/**
 * Estados en los que el admin puede eliminar una lista: los mismos que acepta
 * DELETE /inventarios/:id en el backend. COMPLETADO y REVISADO quedan como
 * resultado auditable del inventario
 */
export const ESTADOS_ELIMINABLES = [INVENTARIO_ESTADO.PENDIENTE, INVENTARIO_ESTADO.EN_CURSO];

/** Estados que el admin aún tiene pendientes: todos menos REVISADO (contador del header) */
export const ESTADOS_SIN_REVISAR = [INVENTARIO_ESTADO.PENDIENTE, INVENTARIO_ESTADO.EN_CURSO, INVENTARIO_ESTADO.COMPLETADO];

/** Estados en los que el inventario ya no admite conteos: se muestra su resultado */
export const ESTADOS_FINALIZADOS = [INVENTARIO_ESTADO.COMPLETADO, INVENTARIO_ESTADO.REVISADO];

/**
 * Etiqueta y color de badge por estado, usando el sistema unificado
 * BADGE_COLORS (ver CLAUDE.md, "Sistema de Badges Corporativo Unificado").
 * Consumida por components/ui/EstadoInventarioBadge (MI4).
 */
export const ETIQUETAS_ESTADO = {
    [INVENTARIO_ESTADO.PENDIENTE]: { label: "Pendiente", color: BADGE_COLORS.GRAY },
    [INVENTARIO_ESTADO.EN_CURSO]: { label: "En curso", color: BADGE_COLORS.AMBER },
    [INVENTARIO_ESTADO.COMPLETADO]: { label: "Completado", color: BADGE_COLORS.GREEN },
    [INVENTARIO_ESTADO.REVISADO]: { label: "Revisado", color: BADGE_COLORS.BLUE },
};


/** Meses que cubre por defecto el filtro de fechas, hasta hoy incluido */
export const MESES_FILTRO_FECHAS = 1;

// Fecha local YYYY-MM-DD: toISOString() daría el día anterior de madrugada en Canarias
const aFechaLocal = (fecha) =>
    `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`;

/**
 * Filtros iniciales de los listados: el último mes (del mismo día del mes
 * anterior a hoy, ambos incluidos) y sin más filtros. Las fechas son inclusivas y se aplican sobre
 * fechaCreacion. Es función, no constante, porque el rango depende de hoy
 * @param {Date} [hoy] - Fecha de referencia (inyectable para tests)
 * @returns {{estado: string, preparadorId: string, fechaDesde: string, fechaHasta: string}}
 */
export const getFiltrosInventariosIniciales = (hoy = new Date()) => {
    // Sin desbordar: 31 de marzo menos un mes es el 28/29 de febrero, no el 3 de marzo
    const ultimoDiaMesDesde = new Date(hoy.getFullYear(), hoy.getMonth() - MESES_FILTRO_FECHAS + 1, 0).getDate();
    const desde = new Date(hoy.getFullYear(), hoy.getMonth() - MESES_FILTRO_FECHAS, Math.min(hoy.getDate(), ultimoDiaMesDesde));
    return { estado: "", preparadorId: "", fechaDesde: aFechaLocal(desde), fechaHasta: aFechaLocal(hoy) };
};
